import useStore from 'state/knovStore'
import { isMobile } from 'lib/device'
import {
    getCenterIndex,
    getSwiperRef,
    SWIPE_SPEED_FAST,
    SWIPE_SPEED_SLOW,
} from 'state/imperativeApis/swiperApi'
import { v4 as uuid } from 'uuid'
import { Filter } from 'components/filters/useStreamFilters'
import { openPlacementIntentForFilter } from 'lib/threadOpenPlacement'
import { primeQuestForInstantOpen } from 'refactor/panels/primeChildQuestForOpen'

export const centerOpenPlacementIntent = (filter: Filter) => openPlacementIntentForFilter(filter)

type OpenCenterCoreOptions = {
    filter: Filter
    panelId?: string
    meta?: Record<string, unknown>
    ensureUnique?: boolean
}

/**
 * Orchestrates opening a new center panel in 3-panel mode without sliding Swiper.
 * Visual effect:
 * - Left and current center translate left off-screen, right stays fixed
 * - New center panel is inserted into the center slot
 * - Previous left panel is removed (no animation) to keep right visually fixed
 */
export async function openCenterCore({
    filter,
    panelId,
    meta,
    ensureUnique = true,
}: OpenCenterCoreOptions) {
    const panels = useStore.getState().panels
    const numPanels = useStore.getState().numPanels
    const swiper = getSwiperRef()?.current
    // Overlap the quest fetch with the panel work (no-op unless instant_reply_nav is on
    // and the cache can't already produce the target's row).
    if (filter?.questId && filter?.answerId) {
        primeQuestForInstantOpen(filter.questId, filter.answerId)
    }

    // Mobile / 1-panel fallback: simply replace the center panel content and slide if needed
    if (isMobile || numPanels === 1) {
        const centerIndex = getCenterIndex()
        const current = panels.getPanels()[centerIndex]
        if (current) {
            // A quest switch remounts the container, so a fresh target can place pre-paint.
            // Retargeting within the already-visible quest keeps the animated reveal.
            const placementIntent =
                current.filter?.questId !== filter?.questId
                    ? centerOpenPlacementIntent(filter)
                    : undefined
            panels.editPanel(current.panelId, {
                filter,
                empty: false,
                ...(meta || {}),
                ...(placementIntent ? { pendingOpenPlacement: placementIntent } : {}),
            })
            // Optional: shake to indicate update
            setTimeout(() => panels.getPanel(current.panelId)?.shake?.(), 150)
        }
        return { panelId: current?.panelId || panelId || uuid(), existed: !!current }
    }

    // In 3-panel mode, animate insert at the current center index
    // Ensure bookend empties exist before manipulating structure to avoid SPV flicker
    if (!isMobile && numPanels !== 1) {
        let list = panels.getPanels()
        if (!list[0]?.empty) panels.prependEmptyPanel()
        list = panels.getPanels()
        if (!list[list.length - 1]?.empty) panels.appendEmptyPanel()
    }

    const centerIndex = getCenterIndex()
    const prevActiveIndex = swiper?.activeIndex ?? 0

    // If we are opening the same quest at center, just shake/highlight
    if (ensureUnique && filter?.questId) {
        const center = panels.getPanels()[centerIndex]
        if (center && !center.empty && center.filter?.questId === filter.questId) {
            if (meta && Object.keys(meta).length) {
                panels.editPanel(center.panelId, meta)
            }
            // Update answerId if provided
            if (filter?.answerId) {
                panels.editPanel(center.panelId, {
                    filter: { ...center.filter, answerId: filter.answerId },
                })
            }
            // Nudge the center
            setTimeout(() => {
                if (filter?.answerId) {
                    void panels.getPanel(center.panelId)?.requestAnswerReveal?.(filter.answerId, {
                        questId: filter.questId,
                        shake: true,
                        highlight: true,
                    })
                } else {
                    panels.getPanel(center.panelId)?.shake?.()
                }
            }, 0)
            return { panelId: center.panelId, existed: true as const }
        }
    }

    // Insert new panel at center with animated left shift; keep right fixed.
    // Always remove previous left-of-center so the viewport shifts left and the
    // left-of-center (swiper.activeIndex) increments by 1.
    const newPanelId = panelId || uuid()
    const placementIntent = centerOpenPlacementIntent(filter)
    useStore.getState().panels.startAnimatedInsert(
        centerIndex,
        {
            panelId: newPanelId,
            ...(meta || {}),
            filter,
            empty: false,
            ...(placementIntent ? { pendingOpenPlacement: placementIntent } : {}),
        },
        {
            direction: 'left',
            actionParams: {
                ...(meta || {}),
                openCenter: true,
                prevActiveIndex,
                insertAtIndex: centerIndex + 1,
                animateRemove: false,
            },
        },
    )

    // Optionally, we can schedule a gentle nudge/highlight after the animation sequence
    // since we are not sliding Swiper here.
    const waitAndAct = () => {
        const maxAttempts = 20
        let attempts = 0
        const poll = () => {
            const p = panels.getPanel(newPanelId)
            if (p && !p.empty) {
                if (filter?.answerId) {
                    void p.ensureAnswerReveal?.(filter.answerId, {
                        questId: filter.questId,
                        shake: true,
                        highlight: true,
                    })
                } else {
                    p.shake?.()
                }
            } else if (attempts++ < maxAttempts) {
                setTimeout(poll, 150)
            }
        }
        poll()
    }

    setTimeout(waitAndAct, 400)

    return { panelId: newPanelId, existed: false as const }
}
